import { createApi } from '@reduxjs/toolkit/query/react';

import { generateBaseQuseryWithReAuth } from '@/store/baseQueryReauth';
import { CustomerAddress } from '@/types/Address';
import { ResponseError } from '@/types/Error';
import { PawPoints } from '@/types/PawPoints';
import { SuccessResponse } from '@/types/Success';

const contentTypes = {
  applicationJson: 'application/json',
};

export const accountSettingsApi = createApi({
  reducerPath: 'accountSettingsApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: `Account` }),
  tagTypes: ['CustomerAddresses', 'PawPoints', 'EarningsAccount'],
  endpoints: builder => ({
    getCustomerAllAddresses: builder.query<CustomerAddress[], void>({
      query: () => ({ url: `/CustomerAddresses`, method: 'GET', next: { tags: ['CustomerAddresses'] } }),
      transformResponse: (response: CustomerAddress[], meta, arg) => response,
      providesTags: ['CustomerAddresses'],
    }),
    saveAddress: builder.mutation<
      SuccessResponse<CustomerAddress>,
      { address: CustomerAddress; makePrimary?: boolean }
    >({
      query: ({ address, makePrimary }) => ({
        url: `/SaveAddress?makePrimary=${makePrimary}`,
        method: 'POST',
        body: JSON.stringify({ ...address }),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<CustomerAddress>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['CustomerAddresses'] : []),
    }),
    updateAddress: builder.mutation<
      SuccessResponse<CustomerAddress>,
      { address: CustomerAddress; makePrimary?: boolean }
    >({
      query: ({ address, makePrimary }) => ({
        url: `/UpdateAddress?makePrimary=${makePrimary}`,
        method: 'POST',
        body: JSON.stringify({ ...address }),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<CustomerAddress>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['CustomerAddresses'] : []),
    }),

    deleteCustomerAddress: builder.mutation<SuccessResponse<string>, number | undefined>({
      query: addressType => ({
        url: `/DeleteCustomerAddress?type=${addressType}`,
        method: 'POST',
      }),
      transformResponse: (response: SuccessResponse<string>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['CustomerAddresses'] : []),
    }),

    getPawPoints: builder.query<SuccessResponse<PawPoints>, void>({
      query: () => ({ url: `/GetPawPoints`, method: 'GET', next: { tags: ['PawPoints'] } }),
      transformResponse: (response: SuccessResponse<PawPoints>, meta, arg) => response,
      providesTags: ['PawPoints'],
    }),
    getEarningAccount: builder.query<SuccessResponse<PawPoints>, void>({
      query: () => ({ url: `/EarningsAccount?pointAccountID=2`, method: 'GET', next: { tags: ['EarningsAccount'] } }),
      transformResponse: (response: SuccessResponse<PawPoints>, meta, arg) => response,
      providesTags: ['EarningsAccount'],
    }),
  }),
});

export const {
  useGetCustomerAllAddressesQuery,
  useSaveAddressMutation,
  useUpdateAddressMutation,
  useDeleteCustomerAddressMutation,
  useGetPawPointsQuery,
  useGetEarningAccountQuery,
} = accountSettingsApi;
